const MAX_INTEGER_DIGITS = 9;
const MAX_FRACTION_DIGITS = 2;

/**
 * Normalize free-typed or pasted text into a valid partial USD amount for the billing Amount inputs.
 *
 * Run on every input event — which fires for keystrokes *and* paste — so the field is always in a
 * valid state rather than validated after the fact.
 *
 * Rules enforced:
 * - digits and a single decimal separator only; `-`, `+`, `e`, letters and spaces are dropped, so
 *   there are no negatives and no scientific notation;
 * - a comma is accepted as a decimal separator and folded to a dot, because USD renders with a dot;
 * - at most one separator;
 * - no leading zeros on the integer part beyond a single `0` (`0.99` stays, `007.99` becomes `7.99`);
 * - the integer part is capped at {@link MAX_INTEGER_DIGITS};
 * - at most {@link MAX_FRACTION_DIGITS} fractional digits — extra digits are truncated, not rounded.
 *
 * A trailing separator survives (`'12.'`), because the user may still be typing the cents.
 *
 * Ported from Clarity `apps/web/src/shared/utils/amountInput.ts`, behaviour unchanged. Its open
 * questions travel with it: a comma is a decimal separator rather than a thousands separator, the
 * integer cap is a client-side choice the backend does not impose, cents are optional.
 */
export function sanitizeAmountInput(raw: string): string {
	const cleaned = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');

	const firstDot = cleaned.indexOf('.');
	const hasDot = firstDot !== -1;

	const rawInteger = hasDot ? cleaned.slice(0, firstDot) : cleaned;
	const integerPart = rawInteger.replace(/^0+(?=\d)/, '').slice(0, MAX_INTEGER_DIGITS);

	if (!hasDot) {
		return integerPart;
	}

	const fractionPart = cleaned
		.slice(firstDot + 1)
		.replace(/\./g, '')
		.slice(0, MAX_FRACTION_DIGITS);

	return `${integerPart}.${fractionPart}`;
}
